import React, { useState } from 'react'
import { toast } from 'react-toastify'

import SaveIcon from '../../../assets/img/card-decor-5.svg?react'
import DeleteIcon from '../../../assets/img/delete-icon.svg?react'
import EditIcon from '../../../assets/img/edit-pan.svg?react'
import {
    useCreateNotificationEventMutation,
    useDeleteNotificationEventMutation,
    useGetAllNotificationEventsQuery,
    useUpdateNotificationEventMutation,
} from '../../../services/notifications/notifications'
import type { INotificationEvent } from '../../../types/notifications'
import Input, { InputStyles } from '../../common/Input'
import Modal from '../../common/Modal'

interface AddEventModalProps {
    isOpen: boolean
    onClose: () => void
    events?: INotificationEvent[]
}

const AddEventModal: React.FC<AddEventModalProps> = ({
    isOpen,
    onClose,
    events,
}) => {
    const [eventName, setEventName] = useState('')
    const [editEvent, setEditEvent] = useState<{
        id: number | null
        name: string
    }>({
        id: null,
        name: '',
    })
    const [createNotificationEvent] = useCreateNotificationEventMutation()
    const [updateNotificationEvent] = useUpdateNotificationEventMutation()
    const [deleteNotificationEvent] = useDeleteNotificationEventMutation()
    const { refetch } = useGetAllNotificationEventsQuery('no-limit')

    const handleAddEvent = async (event: React.FormEvent) => {
        event.preventDefault()
        const res = await createNotificationEvent({ name: eventName }).unwrap()
        if (res.success) {
            setEventName('')
            onClose()
            toast.success(res.message || 'Event created successfully')
        } else {
            toast.error(res.message || 'Something went wrong')
        }
    }

    const handleEdit = (id: number, name: string) => {
        setEditEvent({ id, name })
    }

    const handleSaveEdit = async () => {
        if (!editEvent.id) return
        const res = await updateNotificationEvent({
            id: editEvent.id,
            name: editEvent.name,
        }).unwrap()
        if (res.success) {
            await refetch()
            toast.success('Event updated successfully')
            setEditEvent({ id: null, name: '' })
        } else {
            toast.error(res.message || 'Failed to update event')
        }
    }
    const handleRemove = async (id: number) => {
        const res = await deleteNotificationEvent(id).unwrap()
        if (res.success) {
            await refetch()
            toast.success('Event deleted successfully')
            setEditEvent({ id: null, name: '' })
        } else {
            toast.error(res.message || 'Failed to remove event')
        }
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Add Event">
            <div>
                <div className="custom-form-body mt-lg-3 ">
                    <div className="col-12">
                        <div className="flex items-end w-full gap--md">
                            <Input
                                inputStyle={InputStyles.outline}
                                label="Event Name"
                                placeholder="Enter Name"
                                value={eventName}
                                onChange={(e) => setEventName(e.target.value)}
                            />
                            <button
                                type="button"
                                className="btn btn--primary-gradient btn--lg fullRadius whitespace-nowrap h-fit ml-lg-3"
                                onClick={handleAddEvent}
                            >
                                Add Event
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {events && events.length > 0 && (
                <div className="custom-form-body mt-lg-3">
                    <div className="col-12">
                        <div className="form-group input--lg w-full">
                            <div className="form-group-label">
                                <span className="label">Existing Events</span>
                            </div>
                            <ul>
                                {events.map((event) => (
                                    <li
                                        className="event-list__item"
                                        key={event.id}
                                    >
                                        <Input
                                            inputStyle={InputStyles.outline}
                                            placeholder="Enter Name"
                                            value={
                                                editEvent.id === event.id
                                                    ? editEvent.name
                                                    : event.name
                                            }
                                            onChange={(e) =>
                                                setEditEvent((prev) => ({
                                                    ...prev,
                                                    name: e.target.value,
                                                }))
                                            }
                                            disabled={editEvent.id !== event.id}
                                        />
                                        <div className="event-list__item-actions">
                                            {editEvent.id === event.id ? (
                                                <button
                                                    onClick={handleSaveEdit}
                                                    type="button"
                                                    className="btn-drp-link-success"
                                                >
                                                    <SaveIcon
                                                        width={15}
                                                        height={20}
                                                    />
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() =>
                                                        handleEdit(
                                                            event.id,
                                                            event.name,
                                                        )
                                                    }
                                                    type="button"
                                                >
                                                    <EditIcon
                                                        width={15}
                                                        height={20}
                                                    />
                                                </button>
                                            )}
                                            <button
                                                onClick={async () =>
                                                    handleRemove(event.id)
                                                }
                                                className="btn-drp-link-danger"
                                                type="button"
                                            >
                                                <DeleteIcon
                                                    width={20}
                                                    height={17}
                                                />
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}
            <div className="custom-form-footer justify-end">
                <button
                    type="button"
                    className="btn btn--lg full-radius btn--primary-gradient"
                    onClick={onClose}
                >
                    Close
                </button>
            </div>
        </Modal>
    )
}

export default AddEventModal
